import { useEffect, useState } from "react";
import { storageUrl } from "@/services/config";
import api from "@/services/api";

interface PartnerLogo {
  id: number;
  image_path: string;
  link: string | null;
  orders: number;
  is_active: boolean;
}

// How many times each row's logo set is tiled back-to-back before the loop
// repeats. Must be high enough that even with very few partners, the tiled
// content is comfortably wider than the diagonal strip itself — otherwise
// the strip's background color runs out from under the logos and shows a
// bare gap partway through the scroll.
const REPEAT = 8;
const LOOP_PERCENT = 100 / REPEAT; // keep in sync with REPEAT

export default function PartnersStrip() {
  const [logos, setLogos] = useState<PartnerLogo[]>([]);

  useEffect(() => {
    api
      .get<PartnerLogo[]>("/partners")
      .then((res) => setLogos(res.data || []))
      .catch(() => {});
  }, []);

  if (logos.length === 0) return null;

  // Split into two rows — one rides the yellow diagonal, the other the green one.
  // With very few partners (e.g. just 1) this odd/even split can leave one
  // side completely empty, so fall back to showing the full list on both
  // strips whenever a split would leave one of them with nothing to render.
  const evenLogos = logos.filter((_, i) => i % 2 === 0);
  const oddLogos = logos.filter((_, i) => i % 2 !== 0);
  const row1 = evenLogos.length > 0 ? evenLogos : logos;
  const row2 = oddLogos.length > 0 ? oddLogos : logos;

  const tiled1 = Array.from({ length: REPEAT }, () => row1).flat();
  const tiled2 = Array.from({ length: REPEAT }, () => row2).flat();

  return (
    // translate-y-1/2 shifts the whole band down by exactly half its own
    // height, so it straddles the bottom edge of whatever section renders
    // it: half sits inside the section, half spills below it. The parent
    // section must NOT have `overflow-hidden` itself, or this gets clipped
    // right back off — see HeroSection.tsx (overflow-hidden moved onto the
    // background-image wrapper only, not the whole <section>).
    <div className="absolute bottom-0 left-0 right-0 translate-y-1/2 z-20 h-[130px] md:h-[320px] overflow-hidden pointer-events-none">
      {/*
        Local, self-contained marquee keyframes (rather than relying on any
        globally-defined animate-marquee/-reverse utility) so the loop
        percentage always matches REPEAT above, however many partners come
        back from the API.

        Background color lives on the OUTER strip (full band width), not on
        the scrolling track — otherwise the color only covers as much as the
        logo content and runs out before reaching the edges of the band.

        The two strips are rotated in OPPOSITE directions and anchored right
        at the container's vertical center (offset by a few px, not a %) so
        they cross through the middle like a literal "X".

        IMPORTANT re: the anchor offset (10px / 18px) and rotation angle
        (5deg): these are tuned together with the container height and width
        so that at every x position across the strip, the full band
        thickness stays inside the wrapper — otherwise the band appears to
        taper/cut off near the left and right edges instead of bleeding
        cleanly off both sides. If you change the rotation angle, width, or
        container height, re-check the edges (e.g. screenshot the far
        left/right ~300px) before shipping.
      */}
      <style>{`
        @keyframes ptnrMarquee {
          from { transform: translateX(0); }
          to { transform: translateX(-${LOOP_PERCENT}%); }
        }
        @keyframes ptnrMarqueeReverse {
          from { transform: translateX(-${LOOP_PERCENT}%); }
          to { transform: translateX(0); }
        }
        .ptnr-marquee { animation: ptnrMarquee 10s linear infinite; }
        .ptnr-marquee-reverse { animation: ptnrMarqueeReverse 12s linear infinite; }
      `}</style>

      {/* Yellow diagonal strip — slants "/" */}
      <div className="absolute left-1/2 top-1/2 w-[170%] md:w-[160%] -translate-x-1/2 -translate-y-1/2 -rotate-[5deg] origin-center z-10 overflow-hidden bg-[#B9D0E5] shadow-[0_2px_6px_rgba(0,0,0,0.2)]">
        <div className="flex ptnr-marquee gap-6 md:gap-14 items-center h-[40px] md:h-[74px] px-4 md:px-6 whitespace-nowrap">
          {tiled1.map((logo, i) => (
            <PartnerItem key={`y-${i}`} logo={logo} variant="dark" />
          ))}
        </div>
      </div>

      {/* Green diagonal strip — slants "\", crossing the yellow one to form the X */}
      <div className="absolute left-1/2 top-1/2 w-[170%] md:w-[160%] -translate-x-1/2 -translate-y-1/2 rotate-[5deg] origin-center z-20 overflow-hidden bg-[#79A286] shadow-[0_2px_6px_rgba(0,0,0,0.2)]">
        <div className="flex ptnr-marquee-reverse gap-6 md:gap-14 items-center h-[40px] md:h-[74px] px-4 md:px-6 whitespace-nowrap">
          {tiled2.map((logo, i) => (
            <PartnerItem key={`b-${i}`} logo={logo} variant="light" />
          ))}
        </div>
      </div>
    </div>
  );
}

function PartnerItem({
  logo,
  variant,
}: {
  logo: PartnerLogo;
  variant: "dark" | "light";
}) {
  return (
    <div className="flex items-center gap-10 md:gap-14 flex-shrink-0">
      <a
        href={logo.link || "#"}
        className="pointer-events-auto flex-shrink-0 opacity-90 hover:opacity-100 transition-opacity"
      >
        {/*
          On the green strip, logos sit inside a small white chip instead of
          being CSS-filtered white. A filter depends on the source SVG/PNG
          having real transparency and can silently wash a logo out to
          nothing (a fully-opaque asset, an already-light logo, etc.) — a
          white backing chip guarantees every logo stays visible in its real
          colors no matter what the source asset looks like.
        */}
        {variant === "light" ? (
          <span className="inline-flex items-center rounded-md px-2 py-1 md:px-3 md:py-1.5 shadow-sm">
            <img
              src={storageUrl(logo.image_path)}
              alt="Partner"
              className="h-6 md:h-9 object-contain"
            />
          </span>
        ) : (
          <img
            src={storageUrl(logo.image_path)}
            alt="Partner"
            className="h-6 md:h-9 object-contain"
          />
        )}
      </a>
      <span
        className={`text-base md:text-lg leading-none select-none ${
          variant === "dark" ? "text-black/55" : "text-white/70"
        }`}
        aria-hidden="true"
      >
        ✦
      </span>
    </div>
  );
}
